import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { GenerateProgramResponse } from 'src/screens/OnboardingTrainer/screens/Dashboard/screens/ProgramGenerated/types';
import { ProgramGenerationPayload } from './useSummarizeInstructionProd';

export const useMockGenerateWorkoutProgramCoachProd = () => {
  return useMutation({
    mutationFn: async (payload: ProgramGenerationPayload) => {
      // Simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      const params = payload.parameters;

      // Parse program duration (e.g., '4-weeks' -> 4)
      const durationStr = params.program_duration?.[0] || '4-weeks';
      const weeksMatch = durationStr.match(/\d+/);
      const weeks = weeksMatch ? parseInt(weeksMatch[0], 10) : 4;

      // Parse training schedule
      const schedule = params.training_schedule || ['monday', 'wednesday', 'friday'];
      const total_workout_days = weeks * schedule.length;

      // Generate chronological dates starting from today
      const workoutDates: dayjs.Dayjs[] = [];
      let current = dayjs();

      while (workoutDates.length < total_workout_days) {
        if (schedule.includes(current.format('dddd').toLowerCase())) {
          workoutDates.push(current);
        }
        current = current.add(1, 'day');
      }

      // Exercise pools for randomization
      const warmUpPool = [
        { duration: 60, title: 'Arm circling' },
        { duration: 60, title: 'Jumping jacks' },
        { duration: 60, title: 'High knees' },
        { duration: 60, title: 'Torso twist' },
        { duration: 60, title: 'Leg swings' },
      ];

      const coolDownPool = [
        { duration: 60, title: 'Bird dog crunch' },
        { duration: 60, title: 'Glute bridge' },
        { duration: 60, title: 'Standing kick back' },
        { duration: 60, title: 'Childs pose' },
        { duration: 60, title: 'Cobra stretch' },
      ];

      const movesPool = [
        { title: 'PushUp', weight: '0', minReps: 8, maxReps: 15, sets: 3 },
        { title: 'UpperCut', weight: '0', minReps: 16, maxReps: 24, sets: 2 },
        { title: 'KettlebellSwing', weight: '8', minReps: 10, maxReps: 15, sets: 3 },
        { title: 'Burpees', weight: '0', minReps: 6, maxReps: 12, sets: 3 },
        { title: 'Squats', weight: '0', minReps: 12, maxReps: 20, sets: 4 },
        { title: 'Dumbbell Row', weight: '10', minReps: 8, maxReps: 12, sets: 3 },
      ];

      const getRandomItems = <T>(arr: T[], count: number): T[] => {
        const shuffled = [...arr].sort(() => 0.5 - Math.random());
        return shuffled.slice(0, count);
      };

      // Generate the datetimeList array
      const datetimeList = workoutDates.map((date) => {
        const dailyWarmUp = getRandomItems(warmUpPool, 2);
        const dailyCoolDown = getRandomItems(coolDownPool, 2);
        const dailyMovesDef = getRandomItems(movesPool, 3);

        const dailyMoves = dailyMovesDef.map((moveDef) => {
          const actualReps =
            Math.floor(Math.random() * (moveDef.maxReps - moveDef.minReps + 1)) + moveDef.minReps;
          return {
            title: moveDef.title,
            list: Array.from({ length: moveDef.sets }).map(() => ({
              reps: actualReps.toString(),
              rest: '60s',
              weight: moveDef.weight,
            })),
          };
        });

        return {
          coolDownMoves: dailyCoolDown,
          day: date.format('D'),
          hour: 8,
          minute: 0,
          month: parseInt(date.format('M'), 10), // 1-indexed in dayjs, match sample 5 for May
          moves: dailyMoves,
          selectTime: '08:00',
          warmUpMoves: dailyWarmUp,
          weekday: date.format('dddd'), // e.g. 'Monday'
          weekday2: date.format('ddd'), // e.g. 'Mon'
          weekdayNum: date.day(), // 0 = Sunday, 1 = Monday. Note: The sample had Monday = 1.
          workoutType: (params.training_goal?.[0] || 'increase strength').replace(/-/g, ' '),
          year: date.year(),
          ymd: date.format('YYYY-MM-DD'),
        };
      });

      // Construct metadata
      const chunks_generated = Math.ceil(weeks / 2); // Sample had 2 chunks for 4 weeks
      const chunk_details = [];
      for (let i = 0; i < chunks_generated; i++) {
        chunk_details.push({
          chunk_number: i + 1,
          days_generated: schedule.length * 2, // approximation
          week_range: `${i * 2 + 1}-${(i + 1) * 2}`,
        });
      }

      const createTime = dayjs().toISOString();

      const response: GenerateProgramResponse = {
        result: {
          metadata: {
            chunk_details,
            chunks_generated,
            clientId: payload.clientId || '',
            programId: '',
            total_workout_days,
          },
          program: {
            data: {
              area: (params.training_goal_details?.[0] || 'upper body strength').replace(/-/g, ' '),
              createTime,
              datetimeList,
              duration: weeks.toString(),
              isActive: 'true',
              programmeName: 'Mock Generated Program',
              updateTime: createTime,
            },
          },
        },
      };

      return response;
    },
  });
};
